import { z } from 'zod'
import { UUID_PATTERN } from './ids.ts'

/** The building blocks of the payload schemas (ARCHITECTURE 7.2), shared by `commands.ts` and by the schema files of the later milestones (M16 rule 8:
 *  each part's zod schemas in a file of its own, registered in `commands.ts`). A part's file imports from HERE and never from `commands.ts`: the registry
 *  in `commands.ts` imports the parts' files, so the other direction is a cycle whose first import evaluates before the bindings it needs exist.
 *  Each primitive mirrors the placeholder of the same name in the migrations' `pg_temp.sch*` helpers (@U, @D, @TS, @LABEL, @NAME, @TEXT, @T, @PARAMS). */

/** @U: a UUID in any case. */
export const uuid = z.string().regex(new RegExp(UUID_PATTERN))
/** @TS: an instant as the device writes it (toISOString, with or without milliseconds and offset). */
export const instantText = z.string().min(20).max(40)
/** @D: a local day, YYYY-MM-DD. */
export const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
/** A code: lower case, digits and underscores, starting with a letter, at most `max` characters after the first. */
export const snake = (max: number) => z.string().regex(new RegExp(`^[a-z][a-z0-9_]{1,${max}}$`))
/** @LABEL: a name in both languages. */
export const bothLabels = z.strictObject({ fr: z.string().min(1).max(120), en: z.string().min(1).max(120) })
/** @NAME: a name in at least one language. */
export const someName = z.strictObject({ fr: z.string().min(1).max(120).optional(), en: z.string().min(1).max(120).optional() }).refine((o) => Object.keys(o).length >= 1)
/** @TEXT: a text in at least one language. */
export const someText = z.strictObject({ fr: z.string().min(1).max(4000).optional(), en: z.string().min(1).max(4000).optional() }).refine((o) => Object.keys(o).length >= 1)
/** @T: a local time of day, HH:MM or HH:MM:SS, or null. */
export const localTime = z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/).nullable()
/** A short free note, or null. */
export const note300 = z.string().max(300).nullable()

/** @PARAMS: the setup parameters a task type declares (M7). */
export const PARAM_KINDS = ['text', 'number', 'choice'] as const
export const paramDef = z.strictObject({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,30}$/), label: bothLabels, kind: z.enum(PARAM_KINDS), unit: z.string().max(12).nullable().optional(),
  choices: z.array(z.string().min(1).max(60)).min(1).max(20).optional(),
})
export const paramDefs = z.array(paramDef).max(12)
/** The kinds of area a task type applies to (codes of `ref.area_type`; empty = any). */
export const areaTypes = z.array(snake(30)).max(30)
